<template>
    <v-container v-if="needsProgramSelection && !selectedProgram" class="toggle animated" fluid>
        <v-row v-if="loadingPrograms" class="fill-height">
            <v-col class="d-flex justify-center mt-16">
                <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </v-col>
        </v-row>
        <v-row v-else-if="programs.length === 0" class="fill-height">
            <v-col class="d-flex justify-center mt-16">
                <v-card align="center" class="mt-16" elevation="0">
                    <v-icon size="200">mdi-note-alert</v-icon>
                    <v-card-text class="text-button">No Active Programs Found.</v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col v-for="program in programs" :key="program.id" cols="12" xl="4" md="4">
                <v-card @click="selectProgram(program)" class="pa-2 d-flex flex-column fill-height" elevation="3">
                    <v-card-text>
                        <div class="text-h6 font-weight-black">{{ program.abbrev }}</div>
                        <div class="text-caption text-grey">{{ program.name }}</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>

    <v-container v-else class="toggle animated" fluid>
        <div class="d-flex flex-column flex-md-row justify-start align-left justify-md-space-between align-md-center mb-4 ga-2">
            <v-btn
                v-if="needsProgramSelection"
                prepend-icon="mdi-chevron-left"
                @click="backToPrograms()"
                class="rounded-lg"
                color="grey-darken-1"
                height="40px"
                elevation="2"
            >
                Back
            </v-btn>
            <div class="font-weight-bold text-h6">
                {{ pageTitle }}
            </div>
        </div>

        <v-tabs v-model="activeTab" color="primary" class="mb-4">
            <v-tab value="course">Course Outcome Score Report</v-tab>
            <v-tab value="program">Program Outcome Assessment</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
            <v-window-item value="course" class="pa-1">
                <div v-if="!selectedCourse">
                    <v-row>
                        <v-col cols="12" md="5">
                            <v-text-field
                                v-model="courseSearch"
                                variant="outlined"
                                prepend-inner-icon="mdi-magnify"
                                label="Search Course"
                                density="comfortable"
                                hide-details
                                rounded="lg"
                                color="primary"
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row v-if="loadingCourses">
                        <v-col class="d-flex justify-center mt-8">
                            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                        </v-col>
                    </v-row>
                    <v-row v-else-if="filteredCourses.length === 0">
                        <v-col class="d-flex justify-center mt-8">
                            <v-card align="center" elevation="0">
                                <v-icon size="120">mdi-book-open-blank-variant-outline</v-icon>
                                <v-card-text class="text-button">No Courses Found for this Program.</v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col v-for="course in filteredCourses" :key="course.id" cols="12" xl="4" md="4">
                            <v-card @click="selectCourse(course)" class="pa-2 d-flex flex-column fill-height" elevation="3">
                                <v-card-text>
                                    <div class="text-subtitle-1 font-weight-black">{{ course.code }}</div>
                                    <div class="text-body-2 text-grey-darken-1 mt-1">{{ course.title }}</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <div v-else>
                    <div class="d-flex flex-column flex-md-row justify-start align-left justify-md-space-between align-md-center mb-4 ga-2">
                        <v-btn
                            prepend-icon="mdi-chevron-left"
                            @click="selectedCourse = null; courseReport = null"
                            class="rounded-lg"
                            color="grey-darken-1"
                            height="40px"
                            elevation="2"
                        >
                            Back to Courses
                        </v-btn>
                        <div class="font-weight-bold text-h6">
                            {{ selectedCourse.code }} Course Outcome Score Report
                        </div>
                    </div>

                    <v-row v-if="loadingCourseReport">
                        <v-col class="d-flex justify-center mt-8">
                            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                        </v-col>
                    </v-row>

                    <div v-else-if="courseReport">
                        <v-card class="pa-4 mb-4" rounded="lg" elevation="2">
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <div class="text-caption text-grey-darken-1">Course</div>
                                    <div class="font-weight-bold">{{ courseReport.course.code }} - {{ courseReport.course.title || selectedCourse.title }}</div>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <div class="text-caption text-grey-darken-1">Semester and School Year</div>
                                    <div class="font-weight-bold">{{ $store.state.semester_schoolyear?.semester }} {{ $store.state.semester_schoolyear?.schoolyear }}</div>
                                </v-col>
                            </v-row>
                        </v-card>

                        <v-card rounded="lg" elevation="4">
                            <div class="report-table-wrapper">
                                <table class="report-table">
                                    <thead>
                                        <tr>
                                            <th class="course-section-col">Section</th>
                                            <th class="course-faculty-col">Faculty</th>
                                            <th v-for="outcome in sortedCourseOutcomeSummaries" :key="outcome.course_outcome_id">
                                                <div>{{ outcome.code }} Performance Indicator</div>
                                                <div class="text-caption font-weight-regular">{{ outcome.assessment_tools }}</div>
                                            </th>
                                            <th class="course-performance-col">Course Outcomes Performance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="section in courseReport.sections" :key="section.faculty_load_id">
                                            <td class="course-section-col">{{ section.section_code || 'N/A' }}</td>
                                            <td class="course-faculty-col">{{ section.faculty_name || 'N/A' }}</td>
                                            <td v-for="outcome in sortedCourseOutcomeSummaries" :key="outcome.course_outcome_id">
                                                {{ getOutcomeValue(section, outcome.course_outcome_id) }}
                                            </td>
                                            <td class="course-performance-col">{{ formatMetric(section.average_score) }}</td>
                                        </tr>
                                        <tr class="summary-row">
                                            <td colspan="2">Average Percentage Rating</td>
                                            <td v-for="outcome in sortedCourseOutcomeSummaries" :key="outcome.course_outcome_id">
                                                {{ formatMetric(outcome.average_score) }}
                                            </td>
                                            <td class="course-performance-col">{{ formatMetric(courseReport.summary.course_average) }}</td>
                                        </tr>
                                        <tr>
                                            <td colspan="2">Institute Standard</td>
                                            <td :colspan="sortedCourseOutcomeSummaries.length"></td>
                                            <td class="course-performance-col">
                                                <div class="wrapped-cell">{{ courseReport.summary.institute_standard }}</div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colspan="2">Remarks</td>
                                            <td :colspan="sortedCourseOutcomeSummaries.length"></td>
                                            <td class="course-performance-col">
                                                <div class="wrapped-cell">{{ courseReport.summary.remarks }}</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </v-card>
                    </div>
                </div>
            </v-window-item>

            <v-window-item value="program" class="pa-1">
                <v-row v-if="loadingProgramAssessment">
                    <v-col class="d-flex justify-center mt-8">
                        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                    </v-col>
                </v-row>

                <v-row v-else-if="!programAssessment || programAssessment.assessments.length === 0">
                    <v-col class="d-flex justify-center mt-8">
                        <v-card align="center" elevation="0">
                            <v-icon size="120">mdi-chart-box-outline</v-icon>
                            <v-card-text class="text-button">No assessment data available for this program.</v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-else>
                    <v-col cols="12">
                        <v-tabs v-model="selectedTab" align-tabs="start" color="darkGreen">
                        <v-tab
                            v-for="assessment in programAssessment.assessments"
                            :key="assessment.id"
                            :value="assessment.id"
                            :class="{ 'font-weight-black': selectedTab == assessment.id }"
                        >
                            {{ assessment.code }}
                        </v-tab>
                        </v-tabs>
                        <v-divider></v-divider>
                    </v-col>

                    <v-col cols="12">
                        <v-window v-model="selectedTab">
                        <v-window-item
                            v-for="assessment in programAssessment.assessments"
                            :key="assessment.id"
                            :value="assessment.id"
                        >
                            <v-card class="pa-4" rounded="lg" elevation="4">
                            <div class="text-h6 font-weight-bold mb-4">
                                Assessment Per Program Outcome: {{ assessment.code }}
                            </div>

                            <v-row dense>
                                <v-col cols="12" md="8">
                                <div class="assessment-grid">
                                    <div>
                                    <div class="font-weight-bold mb-2">
                                        Formative (Introductory and Enabling - 40%)
                                    </div>

                                    <div class="report-table-wrapper">
                                        <table class="report-table">
                                        <thead>
                                            <tr>
                                            <th>Course</th>
                                            <th
                                                v-for="code in getOutcomeCodes(assessment.formative_courses)"
                                                :key="code"
                                            >
                                                {{ code }}
                                            </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                            v-for="course in assessment.formative_courses"
                                            :key="'f-' + course.course_code"
                                            >
                                            <td>
                                                {{ course.course_code }} - {{ course.course_title }}
                                            </td>
                                            <td
                                                v-for="code in getOutcomeCodes(assessment.formative_courses)"
                                                :key="code"
                                            >
                                                {{ getCourseOutcomeScore(course, code) }}
                                            </td>
                                            </tr>

                                            <tr class="summary-row">
                                            <td>Average Formative Rating</td>
                                            <td
                                                :colspan="Math.max(getOutcomeCodes(assessment.formative_courses).length, 1)"
                                            >
                                                {{ formatMetric(assessment.formative_average) }}
                                            </td>
                                            </tr>
                                        </tbody>
                                        </table>
                                    </div>
                                    </div>

                                    <div>
                                    <div class="font-weight-bold mb-2">
                                        Summative (Demonstrative - 60%)
                                    </div>

                                    <div class="report-table-wrapper">
                                        <table class="report-table">
                                        <thead>
                                            <tr>
                                            <th>Course</th>
                                            <th
                                                v-for="code in getOutcomeCodes(assessment.summative_courses)"
                                                :key="code"
                                            >
                                                {{ code }}
                                            </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                            v-for="course in assessment.summative_courses"
                                            :key="'s-' + course.course_code"
                                            >
                                            <td>
                                                {{ course.course_code }} - {{ course.course_title }}
                                            </td>
                                            <td
                                                v-for="code in getOutcomeCodes(assessment.summative_courses)"
                                                :key="code"
                                            >
                                                {{ getCourseOutcomeScore(course, code) }}
                                            </td>
                                            </tr>

                                            <tr class="summary-row">
                                            <td>Average Summative Rating</td>
                                            <td
                                                :colspan="Math.max(getOutcomeCodes(assessment.summative_courses).length, 1)"
                                            >
                                                {{ formatMetric(assessment.summative_average) }}
                                            </td>
                                            </tr>
                                        </tbody>
                                        </table>
                                    </div>
                                    </div>

                                </div>
                                </v-col>

                                <v-col cols="12" md="4">
                                <div class="summary-panel">
                                    <div class="summary-box summary-box-green">
                                    <div class="text-subtitle-2 font-weight-bold">
                                        Grand Average for {{ assessment.code }}
                                    </div>
                                    <div class="text-h5 font-weight-black mt-2">
                                        {{ formatMetric(assessment.grand_average) }}
                                    </div>
                                    </div>

                                    <div class="summary-box">
                                    <div class="text-subtitle-2 font-weight-bold">
                                        Institute Standard
                                    </div>
                                    <div class="wrapped-cell mt-2">
                                        {{ assessment.institute_standard }}
                                    </div>
                                    </div>

                                    <div class="summary-box">
                                    <div class="text-subtitle-2 font-weight-bold">
                                        Remarks
                                    </div>
                                    <div class="wrapped-cell mt-2">
                                        {{ assessment.remarks }}
                                    </div>
                                    </div>

                                    <div class="summary-box">
                                    <div class="text-subtitle-2 font-weight-bold">
                                        CQI
                                    </div>
                                    <div class="wrapped-cell mt-2">
                                        {{ assessment.cqi }}
                                    </div>
                                    </div>
                                </div>
                                </v-col>
                            </v-row>
                            </v-card>
                        </v-window-item>
                        </v-window>
                    </v-col>
                    </v-row>
            </v-window-item>
        </v-window>
    </v-container>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
    data() {
        return {
            loadingPrograms: false,
            loadingCourses: false,
            loadingCourseReport: false,
            loadingProgramAssessment: false,
            programs: [],
            courses: [],
            selectedProgram: null,
            selectedCourse: null,
            courseSearch: null,
            activeTab: 'course',
            courseReport: null,
            programAssessment: null,
            selectedTab: null,
        }
    },
    computed: {
        needsProgramSelection() {
            return this.$store.state.currentRole.id === 4
        },
        pageTitle() {
            if (!this.selectedProgram) {
                return 'Outcomes Reports'
            }

            return this.selectedProgram.abbrev + ' - Outcomes Reports'
        },
        filteredCourses() {
            if (!this.courseSearch) {
                return this.courses
            }

            const query = this.courseSearch.toLowerCase().trim()
            return this.courses.filter((course) => {
                const code = (course.code || '').toLowerCase()
                const title = (course.title || '').toLowerCase()
                return code.includes(query) || title.includes(query)
            })
        },
        sortedCourseOutcomeSummaries() {
            if (!this.courseReport?.outcome_summaries) {
                return []
            }

            return [...this.courseReport.outcome_summaries].sort((a, b) => this.compareOutcomeCode(a.code, b.code))
        },
    },
    watch: {
        activeTab(newValue) {
            if (newValue === 'program' && this.selectedProgram && !this.programAssessment) {
                this.loadProgramAssessment()
            }
        },
    },
    methods: {
        async init() {
            if (this.needsProgramSelection) {
                await this.loadPrograms()
                return
            }

            this.selectedProgram = this.$store.state.user.program
            await Promise.all([this.loadCourses(), this.loadProgramAssessment()])
        },
        async loadPrograms() {
            this.loadingPrograms = true
            const res = await this.axiosCall('/programs/findAllProgramByInstitute/' + this.$store.state.user.office.id, 'get', null, true)
            if (res && res.status === 200) {
                this.programs = res.data
            }
            this.loadingPrograms = false
        },
        async selectProgram(program) {
            this.selectedProgram = program
            this.selectedCourse = null
            this.courseReport = null
            this.programAssessment = null
            this.courseSearch = null
            await Promise.all([this.loadCourses(), this.loadProgramAssessment()])
        },
        async loadCourses() {
            if (!this.selectedProgram) {
                return
            }

            this.loadingCourses = true
            const [coursesRes, loadsRes] = await Promise.all([
                this.axiosCall('/course/findAllActiveCourseByProgram/' + this.selectedProgram.id, 'get', null, true),
                this.axiosCall('/faculty-load', 'get', null, true),
            ])

            if (coursesRes && coursesRes.status === 200) {
                const baseCourses = coursesRes.data || []
                const semesterId = Number(this.$store.state.semester_schoolyear?.id)

                const loadCourseCodes = new Set(
                    (loadsRes && Array.isArray(loadsRes.data) ? loadsRes.data : [])
                        .filter((load) => Number(load.program_id) === Number(this.selectedProgram.id))
                        .filter((load) => Number(load.semester_schoolyear_id) === semesterId)
                        .filter((load) => load.status === true)
                        .map((load) => String(load.code || '').trim().toUpperCase())
                        .filter((code) => !!code),
                )

                this.courses = baseCourses.filter((course) =>
                    loadCourseCodes.has(String(course.code || '').trim().toUpperCase()),
                )
            } else {
                this.courses = []
            }
            this.loadingCourses = false
        },
        async selectCourse(course) {
            this.selectedCourse = course
            await this.loadCourseReport()
        },
        async loadCourseReport() {
            if (!this.selectedCourse) {
                return
            }

            this.loadingCourseReport = true
            const res = await this.axiosCall(
                '/reports/course-outcome-score/' + this.selectedCourse.code + '/' + this.$store.state.semester_schoolyear.id,
                'get',
                null,
                true,
            )

            this.courseReport = res && res.status === 200 ? res.data : null
            this.loadingCourseReport = false
        },
        async loadProgramAssessment() {
            if (!this.selectedProgram) {
                return
            }

            this.loadingProgramAssessment = true
            const res = await this.axiosCall(
                '/reports/program-outcome-assessment/' + this.selectedProgram.id + '/' + this.$store.state.semester_schoolyear.id,
                'get',
                null,
                true,
            )

            this.programAssessment = res && res.status === 200 ? res.data : null
            this.loadingProgramAssessment = false
        },
        backToPrograms() {
            this.selectedProgram = null
            this.selectedCourse = null
            this.courses = []
            this.courseReport = null
            this.programAssessment = null
            this.courseSearch = null
            this.activeTab = 'course'
        },
        getOutcomeValue(section, courseOutcomeId) {
            const item = section.outcome_scores.find((outcome) => outcome.course_outcome_id === courseOutcomeId)
            return this.formatMetric(item?.score ?? null)
        },
        getOutcomeCodes(courses) {
            const normalizedCodes = courses
                .flatMap((course) => course.outcome_scores.map((item) => this.normalizeOutcomeCode(item.code)))
                .filter((code) => !!code)

            return [...new Set(normalizedCodes)].sort(
                this.compareOutcomeCode,
            )
        },
        getCourseOutcomeScore(course, code) {
            const targetCode = this.normalizeOutcomeCode(code)
            const item = course.outcome_scores.find(
                (outcome) => this.normalizeOutcomeCode(outcome.code) === targetCode,
            )
            if (!item) {
                return 'N/A'
            }

            return this.formatMetric(item?.score ?? null)
        },
        normalizeOutcomeCode(code) {
            return String(code || '').trim().toUpperCase()
        },
        compareOutcomeCode(a, b) {
            const normalizedA = this.normalizeOutcomeCode(a)
            const normalizedB = this.normalizeOutcomeCode(b)
            const matchA = normalizedA.match(/^(?:CO)?(\d+)$/i)
            const matchB = normalizedB.match(/^(?:CO)?(\d+)$/i)

            if (matchA && matchB) {
                return Number(matchA[1]) - Number(matchB[1])
            }

            return normalizedA.localeCompare(normalizedB, undefined, { numeric: true, sensitivity: 'base' })
        },
        formatMetric(value) {
            return value === null || value === undefined ? '---' : value
        },
    },
    mounted() {
        this.init()
    },
})
</script>

<style scoped>
.report-table-wrapper {
    overflow-x: auto;
}

.report-table {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
}

.report-table th,
.report-table td {
    border: 1px solid #d0d7de;
    padding: 10px;
    vertical-align: top;
}

.report-table th {
    background: #f5f7fa;
    text-align: center;
}

.course-section-col {
    min-width: 130px;
}

.course-faculty-col {
    min-width: 220px;
}

.course-performance-col {
    width: 350px;
    min-width: 350px;
    max-width: 350px;
}

.summary-row {
    background: #eef6ea;
    font-weight: 700;
}

.wrapped-cell {
    white-space: normal;
    min-width: 180px;
}

.assessment-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}

.summary-panel {
    display: grid;
    gap: 12px;
}

.summary-box {
    border: 1px solid #d0d7de;
    border-radius: 12px;
    padding: 16px;
    background: #ffffff;
}

.summary-box-green {
    background: #dff4d7;
}
</style>